import React, { useState, useEffect } from "react";
import { throttle } from "lodash";
import "./StopPredictions.scss";
import tramIcons from "../../images/TramDiagrams/tramicons";
import airconIcon from "./../../images/Icons/aircon.svg";
import accessibilityIcon from "./../../images/Icons/accessibility.svg";
import PredictionTimeSection from "../PredictionTimeSection/PredictionTimeSection";
import BottomMessageSection from "../BottomMessageSection/BottomMessageSection";
import { useAuth } from "../../Context/Auth";

function StopPredictionsOrigin(props) {
  const { stop, routeNo, routeColours, stopPredictionsList, terminus } = props;
  const [predictionRows, setPredictionRows] = useState([]);
    const { isPortrait } = useAuth();
    useEffect(() => {
        let elements = [];
        if (stopPredictionsList) {
            elements = stopPredictionsList
                .filter((prediction) => {
                    //Only create display rows where the RouteNo is truthy
                    return parseInt(prediction.RouteNo);
                })
                .map((prediction, index) => {
                    let _className = "";
                    if (index === stopPredictionsList.length - 1) {
                        _className = "last";
                    } else if (index === 0) {
                        _className = "first";
                    }
                    return (
                        <PredictionRow
                            className={_className}
                            prediction={prediction}
                            routeColours={routeColours.find((obj) => obj.RouteNo == prediction.RouteNo)}
                        />
                    );
                });
        }
        const emptyRow = (
            <div className="prediction-row empty">
                <div className="route-number">
                    <span></span>
                </div>
                <div className="route-info"></div>
                <PredictionTimeSection />
            </div>
        );

        //Append empty rows to pad out the array if needed
        setPredictionRows(elements.concat(Array(3).fill(emptyRow)).slice(0, 3));
    }, [stop, routeNo, routeColours, stopPredictionsList]);

    return (
        <div
            key={stop.trackerId}
            className={`stop-predictions ${isPortrait ? "portrait" : "landscape"}-mode`}
        >
            <div className={"direction" + (terminus ? " terminus" : "")}>
                <h2>{stop.CityDirection}</h2>
            </div>
            <div className="prediction-grid">
                <div className="prediction-headings-background">
                    <div className="prediction-headings">
                        <span>Route</span>
                        <span>To</span>
                        <span>Minutes</span>
                    </div>
                </div>
                {predictionRows[0]}
                {predictionRows[1]}
                {predictionRows[2]}
                <BottomMessageSection
                    stopPredictionsList={stopPredictionsList}
                    trackerId={stop.trackerId || "undefined"}
                />
            </div>
        </div>
    );
}

const getTextWidth = (str, font) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font =
        font ||
        'calc(0.5rem + 1.6vw) -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';
    return context.measureText(str).width;
};

const PredictionRow = ({ prediction, routeColours, className }) => {
    const tramIcon = tramIcons[prediction.TramClass]
        ? tramIcons[prediction.TramClass]
        : tramIcons[prediction.TramClass[0]];
    const { isPortrait } = useAuth();

    useEffect(() => {
        updateWidthAndHeight();
    });

    useEffect(() => {
        window.addEventListener("resize", throttle(updateWidthAndHeight, 500));
        return () => window.removeEventListener("resize", updateWidthAndHeight);
    });

    function updateWidthAndHeight() {
        const routeInfoElAll = document.querySelectorAll(".route-info");
        // this is the adjust for looking better
        const adjustment = 4;
        routeInfoElAll.forEach((routeInfoEl) => {
            const routeDestinationEl = routeInfoEl.querySelector(".route-destination");
            const routeDestinationWrapperEl = routeInfoEl.querySelector(
                ".route-destination-wrapper"
            );
            const routeIconsEl = routeInfoEl.querySelector(".route-icons");
            if (routeDestinationEl !== null && routeIconsEl !== null) {
                const style = getComputedStyle(routeDestinationEl);
                const routeDestinationWidth = routeDestinationEl.clientWidth;
                const routeDestinationHeight = routeDestinationEl.offsetHeight;
                const routeInfoHeight = routeInfoEl.clientHeight;
                const routeIconsHeight = routeIconsEl.offsetHeight;
                const textWidth = getTextWidth(routeDestinationEl.textContent, style.font);
                const textPaddingBottom = parseFloat(style.paddingBottom) || 0;
                const textHeight = routeDestinationHeight - textPaddingBottom;
                let calculatedPadding =
                    routeInfoHeight / 2 - routeIconsHeight - textHeight / 2;
                if (routeInfoHeight > 100) {
                    calculatedPadding = calculatedPadding - adjustment;
                }
                if (textWidth <= routeDestinationWidth) {
                    routeDestinationEl.style.marginBottom = `${calculatedPadding - textPaddingBottom
                        }px`;
                    routeDestinationWrapperEl.classList.remove("double-line");
                } else {
                    routeDestinationEl.style.marginBottom = "initial";
                    routeDestinationWrapperEl.classList.add("double-line");
                }
            }
        });
    }

    return (
        <div
            className={`prediction-row ${className}`}
            key={prediction.VehicleNo + prediction.PredictedArrivalDateTime}
        >
            <div
                className="route-number"
                style={{
                    backgroundColor: routeColours.PIDColour,
                    color: routeColours.TextColour,
                }}
                
            >
                <span>{prediction.HeadBoardRouteNo}</span>
            </div>
            <div className="route-info">
                <div className="route-destination-wrapper">
                    <div
                        className={`route-destination ${isPortrait ? "portrait" : "landscape"
                            }-mode`}
                    >
                        {prediction.Destination.substring(
                            0,
                            process.env.REACT_APP_DESTINATION_CHAR_LIMIT || 36
                        )}
                    </div>
                </div>
                <div className="route-icons">
                    {tramIcon ? <img src={tramIcon} alt={prediction.TramClass} /> : ""}
                    {prediction.IsLowFloorTram ? (
                        <img src={accessibilityIcon} alt="Tram is low floor" />
                    ) : (
                        ""
                    )}
                    {prediction.AirConditioned ? (
                        <img src={airconIcon} alt="Tram has air conditioning" />
                    ) : (
                        ""
                    )}
                </div>
            </div>
            <PredictionTimeSection prediction={prediction} className={className} />
        </div>
    );
};
const StopPredictions = React.memo(StopPredictionsOrigin);
export { StopPredictions };
